/**
 * Typed client for the admin panel API (/admin/*, Phase 7). Same token handling as ApiClient;
 * with the cookie transport it sends `X-Custos-Client: admin` and uses the admin refresh cookie.
 * Every change takes a fresh 2FA code (`totpCode`), except pause and signer lock.
 */
import { ApiClient } from "./client.js";
export class AdminApiClient extends ApiClient {
    constructor(opts) {
        super({
            ...opts,
            clientName: "admin",
            authPaths: { refresh: "/admin/auth/refresh", logout: "/admin/auth/logout" },
        });
    }
    // ── Login ──
    adminLogin(email, password) {
        return this.raw("/admin/auth/login", { method: "POST", body: { email, password } });
    }
    adminTotpSetup(challengeToken) {
        return this.raw("/admin/auth/totp/setup", { method: "POST", body: { challengeToken } });
    }
    async adminTotpEnable(challengeToken, code) {
        await this.acceptTokens(await this.raw("/admin/auth/totp/enable", {
            method: "POST",
            body: { challengeToken, code },
        }));
    }
    async adminLoginTotp(challengeToken, code) {
        await this.acceptTokens(await this.raw("/admin/auth/login/totp", {
            method: "POST",
            body: { challengeToken, code },
        }));
    }
    adminMe() {
        return this.request("/admin/me");
    }
    /** totpCode is needed unless the current password is a temporary one. */
    changeAdminPassword(currentPassword, newPassword, totpCode) {
        return this.request("/admin/me/password", {
            method: "POST",
            body: { currentPassword, newPassword, ...(totpCode ? { totpCode } : {}) },
        });
    }
    // ── Deals by date (D82) ──
    dealReport(filter) {
        return this.request("/admin/reports/deals", { query: { ...filter } });
    }
    dealCsv(filter) {
        const q = new URLSearchParams(Object.entries(filter).filter((kv) => typeof kv[1] === "string" && kv[1] !== ""));
        return this.requestBlob(`/admin/reports/deals.csv?${q.toString()}`);
    }
    // ── Live support chat (D79) ──
    async supportInbox(filter = "OPEN") {
        return (await this.request("/admin/support", { query: { filter } }))
            .conversations;
    }
    async supportUnread() {
        return (await this.request("/admin/support/unread")).unread;
    }
    supportChat(id) {
        return this.request(`/admin/support/${encodeURIComponent(id)}`);
    }
    sendSupport(id, input) {
        return this.request(`/admin/support/${encodeURIComponent(id)}/messages`, {
            method: "POST",
            body: input,
        });
    }
    markSupportRead(id) {
        return this.request(`/admin/support/${encodeURIComponent(id)}/read`, {
            method: "POST",
            body: {},
        });
    }
    assignSupport(id, mine) {
        return this.request(`/admin/support/${encodeURIComponent(id)}/assign`, {
            method: "POST",
            body: { mine },
        });
    }
    closeSupport(id) {
        return this.request(`/admin/support/${encodeURIComponent(id)}/close`, {
            method: "POST",
            body: {},
        });
    }
    reopenSupport(id) {
        return this.request(`/admin/support/${encodeURIComponent(id)}/reopen`, {
            method: "POST",
            body: {},
        });
    }
    supportImage(id) {
        return this.requestBlob(`/admin/support/attachments/${encodeURIComponent(id)}`);
    }
    setDisplayName(displayName, totpCode) {
        return this.request("/admin/me/display-name", {
            method: "PUT",
            body: { displayName, totpCode },
        });
    }
    // ── System messages (D78) ──
    async adminMessages(status = "ALL") {
        return (await this.request("/admin/messages", { query: { status } }))
            .messages;
    }
    createMessage(input, totpCode) {
        return this.request("/admin/messages", { method: "POST", body: { ...input, totpCode } });
    }
    updateMessage(id, input, totpCode) {
        return this.request(`/admin/messages/${encodeURIComponent(id)}`, {
            method: "PUT",
            body: { ...input, totpCode },
        });
    }
    /** One click — ending is always safe. */
    endMessage(id) {
        return this.request(`/admin/messages/${encodeURIComponent(id)}/end`, { method: "POST" });
    }
    // ── Staff and roles (Owner only, D77) ──
    async staff() {
        return (await this.request("/admin/staff")).staff;
    }
    async roles() {
        return (await this.request("/admin/roles")).roles;
    }
    createStaff(input, totpCode) {
        return this.request("/admin/staff", { method: "POST", body: { ...input, totpCode } });
    }
    setStaffRole(id, roleId, totpCode) {
        return this.request(`/admin/staff/${encodeURIComponent(id)}/role`, {
            method: "PUT",
            body: { roleId, totpCode },
        });
    }
    setStaffDisabled(id, disabled, totpCode) {
        return this.request(`/admin/staff/${encodeURIComponent(id)}/disable`, {
            method: "POST",
            body: { disabled, totpCode },
        });
    }
    resetStaffPassword(id, tempPassword, totpCode) {
        return this.request(`/admin/staff/${encodeURIComponent(id)}/reset-password`, {
            method: "POST",
            body: { tempPassword, totpCode },
        });
    }
    resetStaff2fa(id, totpCode) {
        return this.request(`/admin/staff/${encodeURIComponent(id)}/reset-2fa`, {
            method: "POST",
            body: { totpCode },
        });
    }
    createRole(input, totpCode) {
        return this.request("/admin/roles", { method: "POST", body: { ...input, totpCode } });
    }
    updateRole(id, input, totpCode) {
        return this.request(`/admin/roles/${encodeURIComponent(id)}`, {
            method: "PUT",
            body: { ...input, totpCode },
        });
    }
    deleteRole(id, totpCode) {
        return this.request(`/admin/roles/${encodeURIComponent(id)}`, {
            method: "DELETE",
            body: { totpCode },
        });
    }
    // ── Dashboard ──
    overview() {
        return this.request("/admin/overview");
    }
    async alerts(all = false) {
        return (await this.request("/admin/alerts", { query: { all: String(all) } }))
            .alerts;
    }
    // ── Users ──
    users(search = "", page = 0) {
        return this.request("/admin/users", { query: { search, page } });
    }
    user(id) {
        return this.request(`/admin/users/${encodeURIComponent(id)}`);
    }
    userLedger(id, page = 0) {
        return this.request(`/admin/users/${encodeURIComponent(id)}/ledger`, {
            query: { page },
        });
    }
    setUserBlocked(id, blocked, reason, totpCode) {
        return this.request(`/admin/users/${encodeURIComponent(id)}/block`, {
            method: "POST",
            body: { blocked, reason, totpCode },
        });
    }
    setKycFlag(id, kycFlag, totpCode) {
        return this.request(`/admin/users/${encodeURIComponent(id)}/kyc`, {
            method: "POST",
            body: { kycFlag, totpCode },
        });
    }
    setUserLimits(id, input, totpCode) {
        return this.request(`/admin/users/${encodeURIComponent(id)}/limits`, {
            method: "PUT",
            body: { ...input, totpCode },
        });
    }
    // ── Audit ──
    audit(filter) {
        return this.request("/admin/audit", {
            query: {
                actorType: filter.actorType,
                action: filter.action,
                target: filter.target,
                page: filter.page ?? 0,
            },
        });
    }
    // ── Queues ──
    async adminWithdrawals(status = "OPEN", userId) {
        return (await this.request("/admin/withdrawals", {
            query: { status, userId },
        })).withdrawals;
    }
    approveWithdrawal(id, totpCode) {
        return this.request(`/admin/withdrawals/${encodeURIComponent(id)}/approve`, {
            method: "POST",
            body: { totpCode },
        });
    }
    rejectWithdrawal(id, reason, totpCode) {
        return this.request(`/admin/withdrawals/${encodeURIComponent(id)}/reject`, {
            method: "POST",
            body: { reason, totpCode },
        });
    }
    markWithdrawalFailed(id, confirm, totpCode) {
        return this.request(`/admin/withdrawals/${encodeURIComponent(id)}/fail`, {
            method: "POST",
            body: { confirm, totpCode },
        });
    }
    async adminDeposits(status = "OPEN", userId) {
        return (await this.request("/admin/deposits", { query: { status, userId } })).deposits;
    }
    // ── Limits & pause ──
    limits() {
        return this.request("/admin/limits");
    }
    setLimits(input, totpCode) {
        return this.request("/admin/limits", {
            method: "PUT",
            body: { ...input, totpCode },
        });
    }
    pauseWithdrawals() {
        return this.request("/admin/withdrawals/pause", { method: "POST" });
    }
    resumeWithdrawals(totpCode) {
        return this.request("/admin/withdrawals/resume", {
            method: "POST",
            body: { totpCode },
        });
    }
    // ── Swaps (Phase 8) ──
    adminSwaps(filter = {}) {
        return this.request("/admin/swaps", {
            query: { status: filter.status ?? "ALL", userId: filter.userId, page: filter.page ?? 0 },
        });
    }
    swapSettings() {
        return this.request("/admin/swaps/settings");
    }
    /** maxPerSwap: dollars like "100", or null for unlimited. feeBps: 0–500 (100 = 1%). */
    setSwapLimits(input, totpCode) {
        return this.request("/admin/swaps/limits", {
            method: "PUT",
            body: { ...input, totpCode },
        });
    }
    pauseSwaps() {
        return this.request("/admin/swaps/pause", { method: "POST" });
    }
    resumeSwaps(totpCode) {
        return this.request("/admin/swaps/resume", { method: "POST", body: { totpCode } });
    }
    // ── Wallets ──
    hotWallets() {
        return this.request("/admin/hot-wallets");
    }
    async reconciliation() {
        return (await this.request("/admin/reconciliation")).rows;
    }
    coldPlan(chain) {
        return this.request(`/admin/cold/${encodeURIComponent(chain)}`);
    }
    sendToCold(chain, input, totpCode) {
        return this.request(`/admin/cold/${encodeURIComponent(chain)}`, { method: "POST", body: { ...input, totpCode } });
    }
    // ── Vault recovery (Phase 9c) ──
    async vaultRecoveryRequests(status = "") {
        const res = await this.request("/admin/vault-recovery", {
            query: { status: status || undefined },
        });
        return res.requests;
    }
    approveVaultRecovery(id, totpCode) {
        return this.request(`/admin/vault-recovery/${encodeURIComponent(id)}/approve`, {
            method: "POST",
            body: { totpCode },
        });
    }
    // ── Owner reveal (D81): LOCKED material only; it is opened in the Owner's browser. ──
    ownerKeyFile() {
        return this.request("/admin/vault-recovery/key-file");
    }
    uploadOwnerKeyFile(keyFile, totpCode) {
        return this.request("/admin/vault-recovery/key-file", {
            method: "PUT",
            body: { keyFile, totpCode },
        });
    }
    removeOwnerKeyFile(totpCode) {
        return this.request("/admin/vault-recovery/key-file", {
            method: "DELETE",
            body: { totpCode },
        });
    }
    revealMaterial(id, confirm, totpCode) {
        return this.request(`/admin/vault-recovery/${encodeURIComponent(id)}/reveal`, {
            method: "POST",
            body: { confirm, totpCode },
        });
    }
    // Quick reveal (D87): login password + 2FA. The password is checked by the server and used in the
    // browser to open the key; it is never stored or logged.
    storeQuickRevealKey(quickKey, password, totpCode) {
        return this.request("/admin/vault-recovery/quick-key", {
            method: "PUT",
            body: { quickKey, password, totpCode },
        });
    }
    removeQuickRevealKey(totpCode) {
        return this.request("/admin/vault-recovery/quick-key", {
            method: "DELETE",
            body: { totpCode },
        });
    }
    quickRevealMaterial(id, password, totpCode) {
        return this.request(`/admin/vault-recovery/${encodeURIComponent(id)}/reveal-quick`, {
            method: "POST",
            body: { password, totpCode },
        });
    }
    markRevealed(id) {
        return this.request(`/admin/vault-recovery/${encodeURIComponent(id)}/revealed`, {
            method: "POST",
            body: {},
        });
    }
    rejectVaultRecovery(id, totpCode) {
        return this.request(`/admin/vault-recovery/${encodeURIComponent(id)}/reject`, {
            method: "POST",
            body: { totpCode },
        });
    }
    // ── Signer ──
    signer() {
        return this.request("/admin/signer");
    }
    lockSigner() {
        return this.request("/admin/signer/lock", { method: "POST" });
    }
    unlockSigner(passphrase, totpCode) {
        return this.request("/admin/signer/unlock", {
            method: "POST",
            body: { passphrase, totpCode },
        });
    }
}
//# sourceMappingURL=admin.js.map